import { useCallback, useState } from "react";
import dayjs from "dayjs";

import { Button, Link, Typography } from "@mui/material";
import { Check, Close, Edit } from "@mui/icons-material";
import { T } from "../../../../../../util/t";
import { PaymentLink } from "components/layout/payments/PaymentLink";
import { makeStyles } from "../../../../../../providers/makeStyles";
import { FULL_DATE_AND_TIME } from "utils/dates";
import { DialogSectionLayout } from "components/layout/dialogs/athlete/DialogSectionLayout";
import { FieldComponents } from "components/forms/FormKit";
import { FieldProps } from "components/forms/Field";
import { Property } from "graphql/generated/types";

const useStyles = makeStyles((theme) => ({
    labelAndList: {
        display: "flex",
        flexDirection: "row",
        marginTop: theme.spacing(1),
        "& ul": {
            listStyleType: "none",
            padding: 0,
            margin: theme.spacing(0, 0.5),
            a: {
                color: theme.palette.text.link,
            },
            li: {
                display: "flex",
                alignItems: "flex-start",
                "&:not(:last-child)": {
                    marginBottom: theme.spacing(1)
                }
            }
        }
    },
}));
interface AthleteRegistrationDetailsProps {
    control: any
    getValues: any
    setValue: any
    title: any
    entryId: any
    eventDivisionId: any
    memberId: any
    seriesId: any
    entryDate: any
    cancelledDate: any
    payments: any[] | undefined | null
    captureProperties: Property[]
    registrationProperties: Property[]
    basePath: any
}


export const AthleteRegistrationDetails = ({
    control,
    getValues,
    setValue,
    title,
    entryId,
    eventDivisionId,
    memberId,
    seriesId,
    entryDate,
    cancelledDate,
    payments,
    captureProperties,
    registrationProperties,
    basePath
}: AthleteRegistrationDetailsProps) => {
    const [edit, setEdit] = useState(false);

    const formattedEntryDate = dayjs(entryDate).format(FULL_DATE_AND_TIME);
    const formattedCancelledDate = cancelledDate && dayjs(cancelledDate).format(FULL_DATE_AND_TIME);

    const registeredOn = entryId ? "event_divisions" : "series";
    const registrantType = entryId ? "entries" : "members";
    const registrantDocumentPath = `/api/long_running/${registeredOn}/${eventDivisionId || seriesId}/${registrantType}/${entryId || memberId}/documents`;

    const canEdit = Boolean(registrationProperties?.length);
    const nonSignatureProperties = registrationProperties.filter(property => property.type !== "signature");
    const signatureProperties = registrationProperties.filter(property => property.type === "signature");
    const nonSignatureCaptureProperties = captureProperties.filter(p => p.type !== "signature" && p.level === "registration");

    const fields = [
        { uuid: "entry-field", label: "Entry date", value: formattedEntryDate },
        ...(formattedCancelledDate ? [{ uuid: "cancelled-field", label: "Cancelled date", value: formattedCancelledDate }] : []),
        ...nonSignatureProperties
    ];
    const classes = useStyles();

    const triggerEditState = useCallback(() => {
        setEdit(true);
    }, []);

    return (
        <DialogSectionLayout
                title={title || "Athlete details"}
                action={canEdit && !edit &&
                    <Button onClick={triggerEditState} startIcon={<Edit fontSize="small"/>}>
                        <T>Edit</T>
                    </Button>
                }
        >
            {!edit ?
                <>
                    {fields.map(field =>
                        <p key={field.uuid}>
                            <Typography variant="label1"><T>{field.label}</T>:</Typography> {field.value}
                        </p>
                    )}
                    {signatureProperties.length > 0 &&
                            <section className={classes.labelAndList}>
                                <Typography variant="label1" ><T>Signed</T>:</Typography>
                                <ul>
                                    {signatureProperties.map(property =>
                                        <li key={property.uuid}>
                                            {property.value ? <Check fontSize="small"/> : <Close fontSize="small"/>}
                                            {property.value ?
                                                // TODO: this had variant label, which doesn't seem to exist anymore. Assuming label1 for now
                                                // but would be good to do a visual check before switching to the new dialog
                                                <Link variant="label1"
                                                      underline="always"
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      marginLeft={1}
                                                      href={property.value.match("https") ? property.value : `${registrantDocumentPath}/${property.uuid}.pdf`}>

                                                    {property.label}
                                                </Link> :
                                                property.label
                                            }
                                        </li>
                                    )}
                                </ul>
                            </section>}

                    {payments &&
                        <section className={classes.labelAndList} aria-label="Payment">
                            <Typography variant="label1" ><T>Payment</T>:</Typography>
                            <ul>
                                {payments.map(payment => <PaymentLink key={payment.id} payment={payment} />)}
                            </ul>
                        </section>}
                </> :
                <>
                    <InputField label={"Entry date"} value={formattedEntryDate}/>
                    <CustomProperties control={control} getValues={getValues} setValue={setValue} captureFields={nonSignatureCaptureProperties} level="registration"
                                      basePath={basePath}/>
                    {signatureProperties.map(property =>
                        <InputField key={property.uuid} label={property.label} value={property.value || ""}/>
                    )}
                </>
            }
        </DialogSectionLayout>
    );
};


interface InputFieldProps {
    label: string
    value: string
}
export const InputField = ({ label, value }: InputFieldProps) =>
    <div className="formField text">
        <label>{label}</label>
        <div className="input-wrapper">
            <input value={value} disabled={true}/>
        </div>
    </div>;


interface CustomPropertiesProps {
        control: any
        getValues: any
        setValue: any
        captureFields: Property[]
        level: string
        basePath: string
}

const CustomProperties = ({ control, getValues, setValue, captureFields, level, basePath }: CustomPropertiesProps) =>  {
    const fields = (captureFields || []).filter(p => p.level === level).map(property => {
        const field: FieldProps = {
            name: `${basePath}.${property.uuid}`,
            type: property.type === "signature" ? "text" : property.type,
            label: property.label,
            parse: value => value ? { uuid: property.uuid, label: property.label, value } : null,
            format: value => value ? value.value : "",
        };

        if (property.type === "select") {
            field.options = property.config?.options?.map(option => ({ label: option, value: option }));
        }

        return field;
    });

    // @ts-expect-error - trigger and watch should probably be optional in FieldComponents, but there's some cascade effect
    return <FieldComponents fields={fields} control={control} getValues={getValues} setValue={setValue}/>;
};
